import { ReactNode, useContext } from "react";
import { useTheme } from "styled-components";
import { generatePath } from "react-router";
import { AddressModel } from "dash-common";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  useScreenResolution,
  Badge,
  AddressLabel,
  useCheckIfSubscriptionFeatureActiveBroker,
  ViewerContext,
  Icon,
} from "dash-ui";

import { RoutesPath } from "flow-components/routes/routes";
import { Property } from "gql/generated.graphql";
import ProjectStatusRow from "containers/properties/PropertyCard/components/ProjectStatusRow";
import {
  StyledWrapper,
  VerifiedBadgeWrapper,
  ImageContainer,
  StyledImage,
  StyledDashPlaceholder,
  StyledHideDashPlaceholder,
  MainContent,
  StyledText,
} from "./PropertyInfoCard.styles";
import MapPropertyUnitsRow from "./components/MapCardUnitsRow";
import { isPropertyAccessFree } from "common/helpers";
import LockedOverlay from "containers/properties/PropertyCard/components/LockedOverlay";

type Props = {
  property: Property;
  renderOptions?: () => ReactNode;
  label?: string;
  isHidden?: boolean;
  smallScale?: boolean;
};

const PropertyInfoCard = ({
  property,
  label,
  isHidden,
  smallScale = false,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation(["forms"]);
  const { t: tLabels } = useTranslation("common", { keyPrefix: "labels" });
  const { isSubscriptionActive } = useCheckIfSubscriptionFeatureActiveBroker();
  const { broker } = useContext(ViewerContext);

  const {
    devices: { isTablet },
  } = useScreenResolution();

  const files = property.mainPropertyMediaFile?.files;

  return (
    <StyledWrapper
      w="100%"
      data-testid={`property-card-${property?._id}`}
      maxWidth={smallScale ? 310 : "100%"}
      onClick={() => {
        if (isSubscriptionActive) {
          window.open(
            generatePath(RoutesPath.PropertyPreview, {
              id: property._id,
            }),
            "_blank"
          );
        }
      }}
    >
      <VerifiedBadgeWrapper>
        {property.marketArea && (
          <Badge
            title={property?.marketArea?.name}
            bgColor={theme.colors.setOpacity(theme.colors.b50, 70)}
            titleColor={theme.colors.white}
            sm
            semibold={false}
            py={2}
            mr={2}
            square={true}
          />
        )}
        {property.isMarkedAsVerified && isSubscriptionActive && (
          <Badge
            title={tLabels`verified`}
            bgColor={theme.colors.setOpacity(theme.colors.g20, 70)}
            titleColor={theme.colors.white}
            sm={true}
            semibold={false}
            py={2}
            square={true}
          />
        )}
      </VerifiedBadgeWrapper>
      <ImageContainer
        h={smallScale ? 128 : [182, 152, 182, 182]}
        pos="relative"
      >
        {!isSubscriptionActive && !isPropertyAccessFree(property?._id) && (
          <LockedOverlay />
        )}
        {files && files.length > 0 ? (
          <StyledImage file={files[0]} />
        ) : (
          <StyledDashPlaceholder variation="secondary" />
        )}
        {isHidden && (
          <StyledHideDashPlaceholder
            icon="visibilityOff"
            variation="secondary"
            iconProps={{
              color: theme.colors.white,
              size: 32,
            }}
          />
        )}
      </ImageContainer>
      <Flex
        px={[4, 3, 4, 4]}
        py={2}
        flexDirection="column"
        justifyContent="space-between"
      >
        <Flex w="100%" flexDirection="column">
          {label && (
            <Box my={1}>
              <Badge w="fit-content" title={label} type="active" square />
            </Box>
          )}
          <StyledText clamp={1} md semibold color={theme.colors.n90}>
            {property.name}
          </StyledText>
          <MapPropertyUnitsRow
            tooltip={false}
            smallScale={true}
            property={property}
          />
        </Flex>
        <Flex flexDirection="column" w="100%" mt={2} mb={2}>
          <Flex w="100%" flex={1}>
            <AddressLabel
              color={theme.colors.n80}
              address={property?.location.address as AddressModel}
              sm={smallScale}
              tooltip={false}
            />
          </Flex>
          <Flex flex={1} w="100%">
            <ProjectStatusRow
              smallScale={true}
              property={property}
              tooltip={false}
            />
          </Flex>
        </Flex>
      </Flex>
    </StyledWrapper>
  );
};

export default PropertyInfoCard;
